<template>
  <div v-if="$can('view', 'Yacht')">
    <v-row no-gutters>
      <v-col cols="4">
        <status-section
          :yacht="yacht"
          v-on="$listeners"
        />
      </v-col>
      <v-col>
        <div v-if="comments.length > 0">
          <v-alert
            v-for="comment in comments"
            :key="comment.id"
            outlined
            text
            color="orange lighten-3"
            class="mb-1 px-2 py-1 caption"
          >
            <v-row
              no-gutters
              class="px-3"
            >
              <v-col
                cols="2"
                class="grey--text"
              >
                {{ comment.user.name }}
              </v-col>
              <v-col
                class="comment-body grey--text text--darken-3"
                v-html="comment.comment"
              />
              <v-col
                cols="auto"
                class="grey--text"
              >
                {{ comment.updated_at | moment('D MMM YYYY') }}
              </v-col>
            </v-row>
          </v-alert>
        </div>
        <v-row dense>
          <v-col
            cols="8"
          >
            <!--<editor-fold description="Basic Details">-->
            <div class="subtitle-1 pa-4">
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  Basic Details
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    v-if="$can('edit-basic-details', 'Yacht')"
                    text
                    color="grey"
                    rounded
                    small
                    class="ma-0"
                    @click="$emit('edit:yacht-basic-details')"
                  >
                    <v-icon
                      left
                      small
                    >
                      edit
                    </v-icon>
                    Edit
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-card elevation="10">
              <v-card-text>
                <yacht-data-row label="Yacht Name">
                  <span v-if="yacht.name">
                    {{ yacht.name }}
                  </span>
                  <a
                    v-else-if="$can('edit-basic-details', 'Yacht')"
                    href="#"
                    @click.prevent="$emit('edit:yacht-basic-details')"
                  >
                    Add name
                  </a>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
                <yacht-data-row label="Yacht Model">
                  {{ yacht.model.full_label }}
                </yacht-data-row>
                <yacht-data-row label="Yacht Type">
                  {{ yacht.model.type.label }}
                </yacht-data-row>
                <yacht-data-row
                  v-if="$can('view', 'Owner')"
                  label="Yacht owner"
                >
                  <span v-if="yacht.owner">
                    <a
                      href="#"
                      @click.stop="yachtOwnerDetailsDialog = true"
                    >{{ yacht.owner.name }}</a>
                  </span>
                  <a
                    v-else-if="$can('edit-basic-details', 'Yacht')"
                    href="#"
                    @click.prevent="$emit('edit:yacht-basic-details')"
                  >
                    Add yacht owner
                  </a>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
                <yacht-data-row
                  v-if="yacht.status === yachtStatusInUse || yacht.status === yachtStatusPhasedOut"
                  label="Current Base"
                >
                  <span v-if="yacht.current_base">
                    {{ yacht.current_base.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
                <yacht-data-row
                  v-else
                  label="Destination Base"
                >
                  <span v-if="yacht.destination_base">
                    {{ yacht.destination_base.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
                <yacht-data-row
                  label="Ex Factory Date"
                >
                  <span v-if="yacht.ex_factory_date">
                    {{ yacht.ex_factory_date | moment('D MMM YYYY') }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
                <yacht-data-row
                  label="Yacht Flag"
                >
                  <span v-if="yacht.flag_country">
                    <flag
                      :iso="yacht.flag_country.code"
                      :squared="false"
                      class="mr-1"
                    />
                    {{ yacht.flag_country.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
                <yacht-data-row
                  label="Port of Registration"
                >
                  <span v-if="yacht.registration_port">
                    {{ yacht.registration_port }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
              </v-card-text>
            </v-card>
            <!--</editor-fold>-->
          </v-col>
          <v-col
            v-if="$can('view-sale-price', 'Yacht') || $can('view-finance', 'Yacht')"
            cols="8"
          >
            <!--<editor-fold description="Finance Details">-->
            <div class="subtitle-1 pa-4">
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  Finance Details
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    v-if="$can('set-finance-model', 'Yacht')"
                    text
                    color="grey"
                    rounded
                    small
                    class="ma-0"
                    @click="$emit('edit:pricing')"
                  >
                    <v-icon
                      left
                      small
                    >
                      edit
                    </v-icon>
                    Edit
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-card elevation="10">
              <v-card-text>
                <yacht-data-row
                  v-if="$can('view-sale-price', 'Yacht')"
                  label="Base Price"
                >
                  {{ yacht.base_sale_price | currency(yacht.currency) }}
                </yacht-data-row>
                <yacht-data-row
                  v-if="$can('view-sale-price', 'Yacht')"
                  label="Equipment Price"
                >
                  {{ yacht.equipment_sale_price | currency(yacht.currency) }}
                </yacht-data-row>
                <yacht-data-row
                  v-if="$can('view-sale-price', 'Yacht')"
                  label="CDR Price"
                >
                  {{ yacht.cdr_price | currency(yacht.currency) }}
                </yacht-data-row>
                <yacht-data-row
                  v-if="$can('view-sale-price', 'Yacht')"
                  label="Total Annex Price"
                >
                  {{ yacht.total_sale_price | currency(yacht.currency) }}
                  <v-tooltip
                    v-if="yacht.production_year_price_increase"
                    top
                    transition="fade-transition"
                  >
                    <template v-slot:activator="{ on }">
                      <span
                        style="border-bottom: 1px dotted #959595"
                        v-on="on"
                      >
                        (+ {{ yacht.production_year_price_increase | currency(yacht.currency) }})
                      </span>
                    </template>
                    <span>
                      Price increase due to higher model year than the specified model
                    </span>
                  </v-tooltip>
                </yacht-data-row>
                <yacht-data-row label="Pricelist / Model Year">
                  {{ yacht.production_year }}
                </yacht-data-row>
                <yacht-data-row label="Financing Model">
                  <span v-if="yacht.finance_model">
                    {{ yacht.finance_model.label }}
                  </span>
                  <a
                    v-else-if="$can('set-finance-model', 'Yacht')"
                    href="#"
                    @click.prevent="$emit('edit:pricing')"
                  >
                    Set model
                  </a>
                  <span
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </span>
                </yacht-data-row>
              </v-card-text>
            </v-card>
            <!--</editor-fold>-->
          </v-col>
          <v-col
            cols="8"
          >
            <!--<editor-fold description="Basic Yacht Specs">-->
            <div class="subtitle-1 pa-4">
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  Basic Yacht Specs
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    v-if="$can('edit-basic-details', 'Yacht')"
                    text
                    color="grey"
                    rounded
                    small
                    class="ma-0"
                    @click="$emit('edit:yacht-basic-details')"
                  >
                    <v-icon
                      left
                      small
                    >
                      edit
                    </v-icon>
                    Edit
                  </v-btn>
                  <v-btn
                    text
                    color="grey"
                    rounded
                    small
                    class="ma-0"
                    :to="{ name: $route.name.includes('service') ? 'serviceYachtSpecs' : 'yachtSpecs', id: yacht.id }"
                  >
                    <v-icon
                      left
                      small
                    >
                      arrow_forward
                    </v-icon>
                    View All Specs
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-card elevation="10">
              <v-card-text class="pa-4">
                <yacht-data-row label="Hull Number">
                  <span v-if="yacht.hull_number">
                    {{ yacht.hull_number }}
                  </span>
                  <a
                    v-else-if="$can('edit-basic-details', 'Yacht')"
                    href="#"
                    @click.prevent="$emit('edit:yacht-basic-details')"
                  >
                    Add hull number
                  </a>
                  <span
                    v-else
                    class="grey--text"
                  >
                    None
                  </span>
                </yacht-data-row>
                <yacht-data-row label="Factory ID">
                  <span v-if="yacht.factory_id">
                    {{ yacht.factory_id }}
                  </span>
                  <a
                    v-else-if="$can('edit-basic-details', 'Yacht')"
                    href="#"
                    @click.prevent="$emit('edit:yacht-basic-details')"
                  >
                    Add factory ID
                  </a>
                  <span
                    v-else
                    class="grey--text"
                  >
                    None
                  </span>
                </yacht-data-row>
                <yacht-data-row label="Engine(s)">
                  {{ yacht.model.engines_description }}
                </yacht-data-row>
                <yacht-data-row label="Engine Serial Numbers">
                  <v-row
                    v-if="yacht.engine_serial_numbers && yacht.engine_serial_numbers.length > 0"
                    no-gutters
                  >
                    <v-col>
                      <div
                        v-for="(engine, index) in yacht.engine_serial_numbers"
                        :key="'engine_serial_number_' + index"
                      >
                        {{ engine.serial_number || 'not specified' }}
                      </div>
                    </v-col>
                    <v-col cols="auto">
                      <a
                        v-if="$can('edit-basic-details', 'Yacht')"
                        href="#"
                        @click="$emit('edit:serial-numbers')"
                      >
                        Edit
                      </a>
                    </v-col>
                  </v-row>
                  <div v-else>
                    <a
                      v-if="$can('edit-basic-details', 'Yacht')"
                      href="#"
                      @click.prevent="$emit('edit:serial-numbers')"
                    >
                      Add engine serial number(s)
                    </a>
                  </div>
                </yacht-data-row>
                <yacht-data-row
                  v-if="yacht.current_engine_hours && yacht.current_engine_hours.length > 0"
                  label="Engine hours"
                >
                  <div
                    v-for="(engine, index) in yacht.current_engine_hours"
                    :key="'engine_hours' + index"
                  >
                    {{ engine.hours }} h
                  </div>
                </yacht-data-row>
                <yacht-data-row label="Capacity">
                  {{ yacht.model.cabin_count }} cabins, {{ yacht.model.berth_count }} berths
                </yacht-data-row>
                <yacht-data-row label="Dimensions">
                  LOA {{ yacht.model.length | fromMetric('m', 2) }} &middot; B {{
                    yacht.model.beam | fromMetric('m', 2)
                  }} &middot; D {{ yacht.model.draught | fromMetric('m', 2) }}
                </yacht-data-row>
              </v-card-text>
            </v-card>
            <!--</editor-fold>-->
          </v-col>
          <v-col
            v-if="$can('view-agreement-details', 'Yacht')"
            cols="8"
          >
            <!--<editor-fold description="Deal Specifics">-->
            <div class="subtitle-1 pa-4">
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  Agreement Details
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    v-if="$can('set-deal-details', 'Yacht')"
                    text
                    color="grey"
                    rounded
                    small
                    class="ma-0"
                    @click="$emit('edit:deal-specifics')"
                  >
                    <v-icon
                      left
                      small
                    >
                      edit
                    </v-icon>
                    Edit
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-card elevation="10">
              <v-card-text>
                <yacht-data-row
                  v-if="yacht.agreement_details && yacht.agreement_details.vat_rate > 0"
                  label="VAT applies"
                >
                  {{ yacht.agreement_details.vat_rate }}%
                </yacht-data-row>
                <yacht-data-row label="Payment plan">
                  <div v-if="yacht.agreement_details && yacht.agreement_details.first_payment.payments && yacht.agreement_details.first_payment.payments.length > 0">
                    <div
                      v-for="(payment, i) in yacht.agreement_details.first_payment.payments"
                      :key="'payment_plan_'+i"
                    >
                      {{ payment.amount | currency(yacht.currency) }}
                      &middot;
                      by {{ payment.due_date | moment('D MMM YYYY') }}
                      <span v-if="payment.is_deposit">
                        (deposit)
                      </span>
                    </div>
                    <div v-if="yacht.agreement_details.last_payment">
                      {{ yacht.agreement_details.last_payment.amount | currency(yacht.currency) }}
                      &middot;
                      by {{ yacht.agreement_details.last_payment.due_date | moment('D MMM YYYY') }}
                    </div>
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    Payment plan not set
                  </div>
                </yacht-data-row>
                <yacht-data-row
                  v-if="$can('view-insured-value', 'Yacht')"
                  label="Insurance rate to owner"
                >
                  <div v-if="yacht.agreement_details && yacht.agreement_details.insurance_rate_to_owner">
                    {{ yacht.agreement_details.insurance_rate_to_owner }}%
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </div>
                </yacht-data-row>
                <yacht-data-row
                  v-if="$can('view-insured-value', 'Yacht')"
                  label="Insured value"
                >
                  <div v-if="yacht.agreement_details && yacht.agreement_details.insured_value">
                    {{ yacht.agreement_details.insured_value | currency(yacht.currency) }}
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    Not set
                  </div>
                </yacht-data-row>
                <yacht-data-row label="Agreed sales terms">
                  <div v-if="yacht.agreement_details && yacht.agreement_details.special_deals">
                    <a
                      v-if="$can('set-deal-details', 'Yacht')"
                      href="#"
                      @click.prevent="$emit('edit:deal-specifics')"
                    >
                      View sales terms
                    </a>
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    No additional sales terms
                  </div>
                </yacht-data-row>
                <yacht-data-row label="Agreed operational terms">
                  <div v-if="yacht.agreement_details && yacht.agreement_details.operational_terms">
                    <a
                      v-if="$can('set-deal-details', 'Yacht')"
                      href="#"
                      @click.prevent="$emit('edit:deal-specifics')"
                    >
                      View operational terms
                    </a>
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    No additional operational terms
                  </div>
                </yacht-data-row>
              </v-card-text>
            </v-card>
            <!--</editor-fold>-->
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <content-dialog
      v-if="yacht.owner"
      :visible.sync="yachtOwnerDetailsDialog"
      max-width="650px"
      :scrollable="true"
      card-class="blue-grey lighten-5"
      small-title
    >
      <template v-slot:dialogTitle>
        Investor / owner details
      </template>

      <v-card-text class="pa-4 white">
        <yacht-data-row
          label="Name"
          striped
        >
          {{ yacht.owner.name }}
        </yacht-data-row>
        <yacht-data-row
          label="Company"
          striped
        >
          <div v-if="yacht.owner.company_details && yacht.owner.company_details.name">
            {{ yacht.owner.company_details.name }}<br>
            Tax ID: {{ yacht.owner.company_details.id }}
          </div>
          <div
            v-else
            class="grey--text"
          >
            N/A
          </div>
        </yacht-data-row>
        <yacht-data-row
          v-if="yacht.owner.contact_details"
          label="Address"
          striped
        >
          {{ yacht.owner.contact_details.address }}<br>
          {{ yacht.owner.contact_details.post_code }} {{ yacht.owner.contact_details.city }}
        </yacht-data-row>
        <yacht-data-row
          label="Country"
          striped
        >
          {{ yacht.owner.country.name }}
        </yacht-data-row>
        <yacht-data-row
          v-if="yacht.owner.contact_details"
          label="E-mail"
          striped
        >
          {{ yacht.owner.contact_details.email }}
        </yacht-data-row>
        <yacht-data-row
          v-if="yacht.owner.contact_details"
          label="Phone Number"
          striped
        >
          {{ yacht.owner.contact_details.phone }}
        </yacht-data-row>

        <div class="subtitle-2 mt-4 mb-1">
          Self Provided Contact Details
        </div>

        <yacht-data-row
          v-if="yacht.owner.contact_details.investor_contact"
          label="E-mail"
          striped
        >
          {{ yacht.owner.contact_details.investor_contact.email }}
        </yacht-data-row>
        <yacht-data-row
          v-if="yacht.owner.contact_details.investor_contact"
          label="Phone Number"
          striped
        >
          {{ yacht.owner.contact_details.investor_contact.phone_number }}
        </yacht-data-row>

        <div v-if="yacht.owner.contact_details.bank">
          <div class="subtitle-2 mt-4 mb-1">
            Self Provided Bank Details
          </div>
          <yacht-data-row
            label="Bank Name"
            striped
          >
            {{ yacht.owner.contact_details.bank.name }}
          </yacht-data-row>
          <yacht-data-row
            label="Bank Address"
            striped
          >
            {{ yacht.owner.contact_details.bank.address }}
          </yacht-data-row>
          <yacht-data-row
            label="Official Name on Bank Account"
            striped
          >
            {{ yacht.owner.contact_details.bank.official_name }}
          </yacht-data-row>
          <yacht-data-row
            label="Official Address on Bank Account"
            striped
          >
            {{ yacht.owner.contact_details.bank.official_address }}
          </yacht-data-row>
          <yacht-data-row
            label="Bank IBAN / Account Number"
            striped
          >
            {{ yacht.owner.contact_details.bank.iban }}
          </yacht-data-row>
          <yacht-data-row
            label="Routing / ABA"
            striped
          >
            {{ yacht.owner.contact_details.bank.routing_number }}
          </yacht-data-row>
          <yacht-data-row
            label="BIC / Swift"
            striped
          >
            {{ yacht.owner.contact_details.bank.bic }}
          </yacht-data-row>
        </div>
      </v-card-text>

      <template v-slot:dialogActions>
        <v-spacer />
        <v-btn
          color="grey"
          text
          @click="yachtOwnerDetailsDialog = false"
        >
          Close
        </v-btn>
      </template>
    </content-dialog>
  </div>
</template>

<script>
import YachtDataRow from '@/components/business/yachts/details/YachtDataRow'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import StatusSection from '@/components/business/yachts/details/YachtDetailsStatusSection'
import Comments from '@/services/api/Comments'
import ContentDialog from '@/components/helpers/Dialog.vue'
import Yacht from '@/models/Yacht'

export default {
  name: 'Dashboard',
  components: { ContentDialog, StatusSection, YachtDataRow },
  props: {
    yacht: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      installedByData: YachtEquipmentPack.installedByData,
      comments: [],
      yachtOwnerDetailsDialog: false
    }
  },
  computed: {
    currentPack: function () {
      if (!this.currentPackId) return undefined

      return this.yacht.equipment_packs.find(p => p.id === this.currentPackId)
    }
  },
  created() {
    this.yachtStatusInUse = Yacht.STATUS_IN_USE
    this.yachtStatusPhasedOut = Yacht.STATUS_PHASED_OUT

    this.fetchComments()
  },
  methods: {
    fetchComments: async function () {
      try {
        this.comments = await Comments.fetch({
          commentable_type: 'App\\Yacht',
          commentable_id: this.yacht.id,
          is_pinned: true
        })
      } catch (e) {
        throw e
      } finally {
        this.comments.loading = false
      }
    }
  }
}
</script>
